import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FriendCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const FriendCodeModal = ({ isOpen, onClose, onSuccess }: FriendCodeModalProps) => {
  const [friendCode, setFriendCode] = useState('');
  const [gamePseudo, setGamePseudo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFriendCode, setShowFriendCode] = useState(true);
  const [showGamePseudo, setShowGamePseudo] = useState(true);
  const [initialFriendCode, setInitialFriendCode] = useState('');
  const [initialGamePseudo, setInitialGamePseudo] = useState('');

  useEffect(() => {
    const fetchProfileInfo = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('pokemon_id, game_pseudo, username')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setShowFriendCode(!data.pokemon_id || data.pokemon_id.trim() === '');
        setShowGamePseudo(!data.game_pseudo || data.game_pseudo.trim() === '');
        setGamePseudo(data.game_pseudo || data.username || '');
        setInitialFriendCode(data.pokemon_id || '');
        setInitialGamePseudo(data.game_pseudo || '');
      }
    };

    fetchProfileInfo();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation basique du format
    if (showFriendCode) {
      const cleanCode = friendCode.replace(/-/g, '');
      if (cleanCode.length !== 16 || !/^\d+$/.test(cleanCode)) {
        setError('Le code ami doit contenir 16 chiffres');
        setLoading(false);
        return;
      }
    }

    try {
      // Récupérer d'abord la session
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          ...(showFriendCode && friendCode !== initialFriendCode && { pokemon_id: friendCode }),
          ...(showGamePseudo && gamePseudo !== initialGamePseudo && { game_pseudo: gamePseudo })
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating friend code:', err);
      setError('Une erreur est survenue lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const formatFriendCode = (value: string) => {
    // Supprimer tout ce qui n'est pas un chiffre ou un tiret
    const cleanValue = value.replace(/[^\d-]/g, '');
    // Supprimer tous les tirets
    const numbers = cleanValue.replace(/-/g, '');
    // Formater avec des tirets tous les 4 chiffres
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 16; i += 4) {
      if (i > 0) formatted += '-';
      formatted += numbers.slice(i, i + 4);
    }
    return formatted;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg p-6 w-full max-w-md space-y-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold mb-4">Complétez vos informations</h2>
        <p className="text-sm text-gray-600 mb-4">
          Pour créer un échange, veuillez compléter les informations manquantes.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {showFriendCode && (
            <div className="mb-4">
              <label htmlFor="friendCode" className="block text-sm font-medium text-gray-700 mb-1">
                Code ami
              </label>
              <input
                type="text"
                id="friendCode"
                value={friendCode}
                onChange={(e) => setFriendCode(formatFriendCode(e.target.value))}
                placeholder="0000-0000-0000-0000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={19} // 16 chiffres + 3 tirets
              />
            </div>
          )}

          {showGamePseudo && (
            <div className="mt-4">
              <label htmlFor="gamePseudo" className="block text-sm font-medium text-gray-700 mb-1">
                Pseudo sur le jeu Pokémon Pocket
              </label>
              <input
                type="text"
                name="gamePseudo"
                id="gamePseudo"
                value={gamePseudo}
                onChange={(e) => setGamePseudo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 mb-4">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || (showFriendCode && !friendCode) || (showGamePseudo && !gamePseudo)}
              className={`px-4 py-2 text-white rounded-lg ${
                loading || (showFriendCode && !friendCode) || (showGamePseudo && !gamePseudo)
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 